import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Plus, Server, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { projectStatusLabel, useI18n } from '../lib/i18n';
import {
  healthLabel,
  healthTone,
  kindLabel,
  serverTypeLabel,
  statusLabel,
  statusTone,
  type ServerDetail,
  type ServerRow,
  type UrlCounts,
} from '../lib/monitoring';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Detail,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Table,
  Td,
  Th,
  Tr,
  statusTone as projectTone,
  useToast,
} from '../components/ui';
import { MonitorBanner } from '../components/MonitorBanner';

/**
 * Monitoring › Servers (operator, 2026-10-20): our servers and VPSs, and the
 * projects each one runs — picked on the server's form, never on the project.
 * A server with no projects is a general one.
 */
export function MonitoringServersPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['monitoring', 'servers', page, q],
    queryFn: () => api.get<Paginated<ServerRow>>('/monitoring/servers', { page, pageSize: 50, q }),
  });

  return (
    <>
      <PageHeader
        title={t('monitoring.servers.title')}
        subtitle={t('monitoring.servers.subtitle')}
        count={data?.total}
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/monitoring/servers/new')}>
            {t('monitoring.servers.new')}
          </Button>
        }
      />

      <MonitorBanner />

      <Card className="mb-4" padded={false}>
        <div className="p-4">
          <Input
            className="sm:max-w-sm"
            placeholder={t('monitoring.servers.search')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<Server className="h-8 w-8" />}
            title={q ? undefined : t('monitoring.servers.empty')}
            hint={q ? undefined : t('monitoring.servers.emptyHint')}
            action={
              !q && (
                <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/monitoring/servers/new')}>
                  {t('monitoring.servers.new')}
                </Button>
              )
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.name')}</Th>
                  <Th>{t('monitoring.server.provider')}</Th>
                  <Th>{t('monitoring.server.projects')}</Th>
                  <Th>{t('monitoring.server.urls')}</Th>
                  <Th>{t('common.status')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((server) => (
                  <Tr key={server.id} onClick={() => navigate(`/monitoring/servers/${server.id}`)}>
                    <Td>
                      <div className="font-medium">{server.name}</div>
                      <Address server={server} />
                    </Td>
                    <Td className="text-muted">
                      {[server.provider, t(serverTypeLabel(server.type))].filter(Boolean).join(' · ')}
                    </Td>
                    <Td>
                      <ProjectChips server={server} />
                    </Td>
                    <Td>
                      <UrlCountsText counts={server.urlCounts} />
                    </Td>
                    <Td>
                      <Badge tone={healthTone(server.health)} dot>
                        {t(healthLabel(server.health))}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

/** Route: `/monitoring/servers/:id`. */
export function MonitoringServerDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmRemove, setConfirmRemove] = useState(false);

  const { data: server, isLoading, error, refetch } = useQuery({
    queryKey: ['monitoring', 'server', id],
    queryFn: () => api.get<ServerDetail>(`/monitoring/servers/${id}`),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/monitoring/servers/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['monitoring'] });
      toast.success(t('monitoring.server.removed'));
      navigate('/monitoring/servers');
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !server) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div className="mb-4">
        <Link
          to="/monitoring/servers"
          className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('monitoring.servers.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span>{server.name}</span>
            <Badge tone={healthTone(server.health)} dot>
              {t(healthLabel(server.health))}
            </Badge>
          </span>
        }
        subtitle={[server.provider, t(serverTypeLabel(server.type)), server.location].filter(Boolean).join(' · ')}
        action={
          <>
            <Button icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/monitoring/servers/${id}/edit`)}>
              {t('common.edit')}
            </Button>
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => navigate(`/monitoring/urls/new?serverId=${id}`)}
            >
              {t('monitoring.urls.new')}
            </Button>
            <Button variant="ghost" icon={<Trash2 className="h-4 w-4" />} onClick={() => setConfirmRemove(true)}>
              {t('monitoring.server.remove')}
            </Button>
          </>
        }
      />

      <MonitorBanner />

      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('monitoring.server.details')} />
          <dl>
            <Detail label={t('monitoring.server.ip')}>
              <span dir="ltr" className="font-mono text-mono-sm">{server.ipAddress ?? '—'}</span>
            </Detail>
            <Detail label={t('monitoring.server.hostname')}>
              <span dir="ltr" className="font-mono text-mono-sm">{server.hostname ?? '—'}</span>
            </Detail>
            <Detail label={t('monitoring.server.provider')}>{server.provider ?? '—'}</Detail>
            <Detail label={t('common.type')}>{t(serverTypeLabel(server.type))}</Detail>
            <Detail label={t('monitoring.server.location')}>{server.location ?? '—'}</Detail>
          </dl>
          {server.notes && (
            <p className="mt-3 whitespace-pre-line border-t hairline pt-3 text-body-sm text-[var(--text-secondary)]">
              {server.notes}
            </p>
          )}
        </Card>

        <Card>
          <CardHeader title={t('monitoring.server.projects')} subtitle={t('monitoring.server.projectsNote')} />
          {server.projects.length === 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <Badge>{t('monitoring.server.general')}</Badge>
              <span className="text-body-sm text-muted">{t('monitoring.server.generalHint')}</span>
            </div>
          ) : (
            <ul className="divide-y hairline">
              {server.projects.map((project) => (
                <li key={project.id} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0">
                    <span className="me-2 font-mono text-mono-sm text-primary-600 dark:text-primary-400">{project.code}</span>
                    <span className="text-body-sm">{localized(project)}</span>
                  </span>
                  {project.deleted ? (
                    <Badge tone="error">{t('monitoring.server.deletedProject')}</Badge>
                  ) : (
                    <Badge tone={projectTone(project.status)}>{projectStatusLabel(project.status, locale)}</Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card padded={false}>
        <div className="p-5 pb-0">
          <CardHeader
            title={t('monitoring.server.urls')}
            subtitle={<UrlCountsText counts={server.urlCounts} />}
            action={
              server.urls.length > 0 && (
                <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => navigate(`/monitoring/urls/new?serverId=${id}`)}>
                  {t('monitoring.urls.new')}
                </Button>
              )
            }
          />
        </div>
        {server.urls.length === 0 ? (
          <EmptyState
            title={t('monitoring.urls.empty')}
            hint={t('monitoring.server.noUrlsHint')}
            action={
              <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate(`/monitoring/urls/new?serverId=${id}`)}>
                {t('monitoring.urls.new')}
              </Button>
            }
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.name')}</Th>
                <Th>{t('monitoring.url.address')}</Th>
                <Th>{t('common.project')}</Th>
                <Th>{t('monitoring.url.kind')}</Th>
                <Th>{t('common.status')}</Th>
              </tr>
            </thead>
            <tbody>
              {server.urls.map((url) => (
                <Tr key={url.id} onClick={() => navigate(`/monitoring/urls/${url.id}`)}>
                  <Td className="font-medium">{url.name}</Td>
                  <Td>
                    <span dir="ltr" className="font-mono text-mono-sm text-[var(--text-secondary)] break-all">{url.url}</span>
                  </Td>
                  <Td className="text-muted">{url.project ? `${url.project.code} · ${localized(url.project)}` : t('monitoring.url.noProject')}</Td>
                  <Td className="text-muted">{t(kindLabel(url.kind))}</Td>
                  <Td>
                    <Badge tone={statusTone(url.status, url.isPaused)} dot>
                      {t(url.isPaused ? 'monitoring.status.PAUSED' : statusLabel(url.status))}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {confirmRemove && (
        <ConfirmDialog
          title={t('monitoring.server.removeConfirm', { name: server.name })}
          confirmLabel={t('monitoring.server.remove')}
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirmRemove(false)}
        >
          <p>{t('monitoring.server.removeBody', { n: server.urls.length })}</p>
        </ConfirmDialog>
      )}
    </>
  );
}

function Address({ server }: { server: Pick<ServerRow, 'ipAddress' | 'hostname'> }) {
  const parts = [server.ipAddress, server.hostname].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <div dir="ltr" className="mt-0.5 text-start font-mono text-mono-sm text-[var(--text-tertiary)] rtl:text-end">
      {parts.join(' · ')}
    </div>
  );
}

function ProjectChips({ server }: { server: ServerRow }) {
  const { t, localized } = useI18n();
  if (server.projects.length === 0) return <Badge>{t('monitoring.server.general')}</Badge>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {server.projects.map((project) => (
        <Badge key={project.id} tone={project.deleted ? 'error' : 'brand'} title={localized(project)}>
          {project.code}
        </Badge>
      ))}
    </div>
  );
}

/** "3 up · 1 down · 2 not checked yet" — only the states some URL is in. */
export function UrlCountsText({ counts }: { counts: UrlCounts }) {
  const { t } = useI18n();
  if (counts.total === 0) return <span className="text-body-sm text-muted">{t('monitoring.server.noUrls')}</span>;
  const parts: Array<{ n: number; label: string; className: string }> = [
    { n: counts.up, label: t('monitoring.count.up'), className: 'text-success-fg dark:text-success' },
    { n: counts.degraded, label: t('monitoring.count.degraded'), className: 'text-warning-fg dark:text-warning-dark' },
    { n: counts.down, label: t('monitoring.count.down'), className: 'text-error-fg dark:text-error-dark' },
    { n: counts.pending, label: t('monitoring.count.pending'), className: 'text-info-fg dark:text-info-dark' },
    { n: counts.paused, label: t('monitoring.count.paused'), className: 'text-[var(--text-tertiary)]' },
  ].filter((p) => p.n > 0);
  return (
    <span className="text-body-sm">
      {parts.map((part, i) => (
        <span key={part.label}>
          {i > 0 && <span className="text-[var(--text-tertiary)]"> · </span>}
          <span className={part.className}>
            <span className="tabular">{part.n}</span> {part.label}
          </span>
        </span>
      ))}
    </span>
  );
}
